import React, { useState, useEffect } from 'react';
import SmartTable, { SmartTableColumn } from '../../../shared/components/SmartTable';
import SmartTableButtons from '../../../shared/components/SmartTableButtons';
import LayoutWrapper from '../../../shared/components/LayoutWrapper';
import { useAuth } from '../../../shared/hooks/useAuth';
import api from '../../../shared/utils/api';
import ErrorPopup from '../../../shared/components/ErrorPopup';
import WarningPopup from '../../../shared/components/WarningPopup';
import { loadCustomersForTaxHistory, Customer } from '../lib/CustomersLoadEntId';
import { getTokenInfo, logTokenInfo, setupTokens, checkAuthentication } from '../../../config/novaqToken';

interface TaxHistory {
  Oid: string;
  ReportNo: string;
  TaxReportName: string;
  CustomerName: string;
  Tin: string;
  PeriodYear: string;
  Period: string;
  RefReportStatusName: string;
  TaxUserName: string;
  RecievedEmpName: string;
  AssessmentAmount: number;
  CreatedDate: string;
  [key: string]: any;
}




const defaultColumns: SmartTableColumn[] = [

  { key: 'customerId', label: 'Харилцагчийн регистр' },
  { key: 'customerName', label: 'Харилцагчийн нэр' },
  { key: 'taxReportCode', label: 'Тайлангийн код' },
  { key: 'taxReportName', label: 'Тайлангийн нэр' }, 
  { key: 'periodYear', label: 'Тайлант жил' },
  { key: 'periodLabel', label: 'Тайлант хугацаа' },
  { 
    key: 'refReportStatusName', 
    label: 'Тайлангийн төлөв',
    render: (value: any) => {
      if (!value) {
        return <div style={{ color: '#6B7280' }}>-</div>;
      }
      
      // "Хүлээн авсан" утгыг ногоон өнгөөр харуулах
      if (value.includes('Хүлээн авсан') || value.includes('хүлээн авсан')) {
        return <div style={{ color: '#10B981', fontWeight: 'bold' }}>{value}</div>;
      }
      
      // Бусад утгыг хэвийн өнгөөр харуулах
      return <div style={{ color: '#374151' }}>{value}</div>;
    }
  },
  { key: 'assessmentAmount', label: 'Тооцоолсон дүн' },
  { key: 'taxUserName', label: 'Хэрэглэгч' },
  { key: 'recievedEmpName', label: 'Хүлээн авсан ажилтан' },
  { key: 'branchName', label: 'Салбар' },
  
];

const TaxGetHistory: React.FC = () => { 
  const { userAuth } = useAuth();
  const [taxHistories, setTaxHistories] = useState<TaxHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showWarningAlert, setShowWarningAlert] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showColumnSearch, setShowColumnSearch] = useState(false);
  const [showColumnSettings, setShowColumnSettings] = useState(false);


  const fetchTaxHistories = async () => {
    try {
      setLoading(true);
      console.log('🔥 TaxGetHistory: Starting to fetch tax histories for ALL customers...');
      console.log('🔥 Selected year:', selectedYear);
      
      // Customer-ийн жагсаалт авах (хэрэв байхгүй бол)
      if (customers.length === 0) {
        console.log('⚠️ No customers loaded, loading customers first...');
        const loadedCustomers = await loadCustomersForTaxHistory(setCustomers, setSelectedCustomer, setLoading, setErrorMessage, setShowErrorPopup, setWarningMessage, setShowWarningAlert);
        
        // Харилцагчийн мэдээлэл байхгүй үед warning message харуулах
        if (loadedCustomers.length === 0) {
          setWarningMessage('Бүртгэлтэй харилцагч олдсонгүй');
          setShowWarningAlert(true);
        }
        return;
      }
      
      // Бүх харилцагчийн мэдээлэл авах
      const customersWithEntId = customers.filter(customer => customer.EntId || customer.entId);
      console.log(`📊 Found ${customersWithEntId.length} customers with EntId out of ${customers.length} total`);
      
      if (customersWithEntId.length === 0) {
        setErrorMessage('EntId бүхий харилцагч олдсонгүй.');
        setShowErrorPopup(true);
        setTaxHistories([]);
        return;
      }

      // Бүх харилцагчийн тайлангийн түүх авах
      const allFlattenedReports: any[] = [];
      
      for (const customer of customersWithEntId) {
        try {
          const entId = customer.EntId || customer.entId;
          console.log(`🔄 Fetching for customer: ${customer.CustomerName} (${entId})`);
          const apiUrl = `/api/tax-history/${entId}/${selectedYear}`;
          const result = await api.get(apiUrl);

          if (result && result.success) {
            console.log(`✅ Tax histories fetched for ${customer.CustomerName}`);
            
            // Ensure data is an array
            let taxData = result.data;
            if (!Array.isArray(taxData)) {
              if (taxData && typeof taxData === 'object') {
                if (taxData.historyList && Array.isArray(taxData.historyList)) {
                  taxData = taxData.historyList;
                } else if (taxData.reportList && Array.isArray(taxData.reportList)) {
                  taxData = taxData.reportList;
                } else {
                  taxData = [taxData];
                }
              } else {
                taxData = [];
              }
            }
            
            // Flatten the data to extract individual reports
            taxData.forEach((historyItem: any) => {
              if (historyItem.returnPeriods && Array.isArray(historyItem.returnPeriods)) {
                historyItem.returnPeriods.forEach((period: any) => {
                  if (period.reports && Array.isArray(period.reports)) {
                    period.reports.forEach((report: any) => {
                      // Add period information to each report
                      allFlattenedReports.push({
                        ...report,
                        periodYear: period.year,
                        periodLabel: period.periodLabel,
                        taxReportCode: historyItem.taxReportCode,
                        taxReportName: historyItem.taxReportName,
                        taxTypeCode: historyItem.taxTypeCode,
                        customerId: customer.CustomerID,
                        customerName: customer.CustomerName
                      });
                    });
                  }
                });
              }
            });
          } else {
            console.warn(`⚠️ No data for customer: ${customer.CustomerName}`);
          }
        } catch (customerError) {
          console.warn(`⚠️ Error fetching data for customer ${customer.CustomerName}:`, customerError);
          // Continue with other customers even if one fails
        }
      }
      
      console.log(`✅ Total reports fetched: ${allFlattenedReports.length}`);
      setTaxHistories(allFlattenedReports);
      
      // Тайлангийн түүх олдохгүй үед шар alert харуулах
      if (allFlattenedReports.length === 0) {
        setWarningMessage('Тайлангийн түүх олдсонгүй');
        setShowWarningAlert(true);
      } else {
        setShowWarningAlert(false);
      }
    } catch (error: any) {
      console.error('❌ Error fetching tax histories:', error);
      console.error('❌ Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      
      let errorMessage = 'Тайлангийн түүх авахад алдаа гарлаа';
      
      if (error.response?.status === 404) {
        errorMessage = 'API endpoint олдсонгүй. Сервертэй холбогдож чадсангүй.';
        // 404 алдаа гарсан үед warning message харуулах
        console.log('⚠️ 404 error occurred, showing warning message');
        setWarningMessage('Бүртгэлтэй харилцагч олдсонгүй');
        setShowWarningAlert(true);
      } else if (error.response?.status === 401) {
        errorMessage = 'Etax системтэй холбогдох алдаа. Татварын системийн нэвтрэлт шаардлагатай.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Серверийн алдаа. Дахин оролдоно уу.';
      } else if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
        errorMessage = 'Сүлжээний алдаа. Сервер ажиллаж байгаа эсэхийг шалгана уу.';
      }
      
      setErrorMessage(errorMessage);
      setShowErrorPopup(true);
    } finally {
      setLoading(false);
    }
  };

  // Button handlers
  const handleToggleSearch = () => {
    setShowColumnSearch(prev => !prev);
  };

  const handleColumnSettings = () => {
    setShowColumnSettings(prev => !prev);
  };

  const handleRefresh = () => {
    console.log('🔄 Сэргээх товч дарагдаж, өгөгдөл дахин ачаалж байна...');
    fetchTaxHistories();
  };

  useEffect(() => {
    // Эхлэхэд customer-ийн жагсаалт авах
    loadCustomersForTaxHistory(setCustomers, setSelectedCustomer, setLoading, setErrorMessage, setShowErrorPopup);
  }, []);

  useEffect(() => {
    // Token шалгах
    const auth = checkAuthentication();
    if (!auth.isAuthenticated) {
      console.log('❌ No valid token found');
      return;
    }
    
    // Customer-ийн жагсаалт амжилттай авагдсаны дараа бүх харилцагчийн тайлангийн түүх авах
    if (customers.length > 0) {
      fetchTaxHistories();
    }
  }, [selectedYear, customers]);

  return (
    <LayoutWrapper userAuth={userAuth}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Тайлангийн түүх</h1>
          
          {/* Action Buttons */}
          <SmartTableButtons
            showColumnSearch={showColumnSearch}
            onToggleSearch={handleToggleSearch}
            onColumnSettings={handleColumnSettings}
            onRefresh={handleRefresh}
            addable={false}
          />
        </div>

      

        {/* Year Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Тайлант жил сонгох (Бүх харилцагчийн тайлангийн түүх)
          </label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {Array.from({ length: 10 }, (_, i) => {
              const year = new Date().getFullYear() - i;
              return (
                <option key={year} value={year.toString()}>
                  {year}
                </option>
              );
            })}
          </select>
        </div>

        <SmartTable
          data={taxHistories}
          columns={defaultColumns}
          fetchData={fetchTaxHistories}
          settingsKey="tax-history-table"
          editable={false}
          addable={false}
          rowKey="reportNo"
          showColumnSearch={showColumnSearch}
          showColumnSettings={showColumnSettings}
        />
        


        <ErrorPopup
          isVisible={showErrorPopup}
          message={errorMessage}
          onClose={() => setShowErrorPopup(false)}
        />
        
        {/* Warning Popup - Тайлангийн түүх олдохгүй эсвэл харилцагч олдохгүй үед */}
        <WarningPopup
          isVisible={showWarningAlert}
          message={warningMessage}
          onClose={() => setShowWarningAlert(false)}
          duration={5000}
        />
      </div>
    </LayoutWrapper>
  );
};

export default TaxGetHistory;
